(ns arthur.follow
  "Following someone: click their face and go where they go, and see each
  other's pointers wherever they are — on the stage, over a button, in a list.

  A LAYER, NOT A PART OF WHAT IT FOLLOWS. It reaches the rest of the app through
  doors that are open anyway — the room's messages (`collab/listen!`,
  `collab/say`, `collab/send!`), app-db, and the page's DOM — and it is switched
  on by `install!` in `arthur.core`. Taking it out is that line, this namespace
  and `follow/`, and the lines marked `follow` in `ui/share`, `app.css` and
  `clips/consumers.py`.

  WHAT IS FOLLOWED is a short list, `facets`, and each can be let go of on its
  own from the popover beside the faces — Live Share's and Figma's answer to
  wanting to look around without leaving: stay together on the tab and frame,
  say, and keep your own selection. Letting go works both ways. What you do not
  take from them you do not send them either, so looking around never drags the
  party along.

  Everyone in a party drives — see `follow/party`, tl's parties — so the moves
  that go out are the deliberate ones. Every member plays the document off its
  own clock, and a play goes out once with its frame rather than streamed."
  (:require [arthur.events.collab :as collab]
            [arthur.events.playback :as pb]
            [arthur.events.ui :as ui]
            [arthur.follow.page :as page]
            [arthur.follow.party :as party]
            [arthur.ui.menu :as menu]
            [arthur.ui.icon :as icon]
            [cognitect.transit :as t]
            [re-frame.core :as rf]
            [re-frame.db :as rf-db]))

(def facets
  "What following someone takes from them: a key, what it is called, and the
  app-db paths it carries. In a path, `::open` stands for the open tab and
  `::clip` for the loaded document, which is a different handle in every
  browser. The two with no paths are the page's, not the db's."
  [[:place     "tab, frame & play" [[:ui :open] [:playback :frame] [:playback :playing?]
                                    [:playback :rate] [:playback :loop?]
                                    [:playback :range ::open]]]
   ;; So that the inspector under their pointer shows what it shows for them.
   [:selection "selection"         [[:ui :selection] [:ui :selections]]]
   ;; So that the row under their pointer is the same row, and the stage
   ;; shows what their solo leaves on it.
   [:rows      "timeline rows & solo" [[:ui :expanded] [:ui :solo ::open]]]
   ;; Ways of looking at the stage. Each person's own until they are following
   ;; — and then watching someone set the onion up is how you learn it.
   [:looks     "onion, tracing & mat" [[:ui :onion ::clip ::open] [:ui :tracing]
                                       [:ui :passepartout]]]
   [:tools     "tool & brush"      [[:ui :tool] [:ui :brush] [:ui :tone] [:ui :auto-key?]]]
   ;; Which panes are shut, so the panel under their pointer is on screen here.
   [:panels    "panels"            [[:ui :layout :shut]]]
   [:scroll    "panel scroll"      nil]
   [:pointers  "pointers"          nil]])

(defn- facet-paths [k] (some (fn [[fk _ ps]] (when (= fk k) ps)) facets))

(def ^:private takes
  "Values taken the way the app takes them, not written over, because writing
  would skip what it does on the way: a new tool commits the pen's unfinished
  polygon, and a selection carries the multi-selection along with it."
  {[:ui :tool]      ui/with-tool
   [:ui :selection] ui/selected})

;; ---------------------------------------------------------------------------
;; what we keep
;;
;; `[:presence :party]` and `[:presence :joinable]` are what we tell the room;
;; `collab` says them again when the socket comes back, which is the rejoin.
;; `:follow` is ours alone: the facets let go of, a move we could not take yet
;; (`:parked`), and `:last`, everything mates have said of where they are, to
;; catch up to on taking a facet back.

(def ^:private stored {:joinable "arthur/followable" :off "arthur/follow-off"})

(defn- recall [k]
  (try (.getItem js/localStorage (stored k)) (catch :default _ nil)))

(rf/reg-fx
 ::remember
 (fn [[k v]] (try (.setItem js/localStorage (stored k) v) (catch :default _ nil))))

(defn- off [db] (get-in db [:follow :off] #{}))

(defn- me [db] (:peer-cid db))
(defn- mine [db] (party/party-of (:peers db) (me db)))
(defn- together? [db] (party/in-party? (:peers db) (me db)))

(rf/reg-event-db
 ::init
 (fn [db _]
   (-> db
       (assoc-in [:presence :joinable] (not= "0" (recall :joinable)))
       (assoc-in [:follow :off] (into #{} (keep keyword) (some-> (recall :off) (.split ",")))))))

;; ---------------------------------------------------------------------------
;; the view a party shares

(def ^:private writer (t/writer :json))
(def ^:private reader (t/reader :json))

(defn- paths [db]
  (into [] (comp (remove (fn [[k]] (contains? (off db) k))) (mapcat last)) facets))

(defn- at
  "Path template `p` in `db`."
  [db p]
  (mapv #(case % ::open (get-in db [:ui :open]) ::clip (:clip/current db) %) p))

(defn- view
  "The part of `db` a party shares, as far as we are following — or as far as
  `along` says, so that letting go of a facet is not itself a move."
  ([db] (view db (paths db)))
  ([db along] (into {} (map (juxt identity #(get-in db (at db %)))) along)))

(defn- view-msg
  "A move, and the tab it was made on whether or not it moves us there: a
  selection, a row or a mark only means something on its own tab."
  [db]
  {:kind "nav" :view (t/write writer (assoc (view db) ::tab (get-in db [:ui :open])))})

(rf/reg-fx ::nav! (page/every 120 collab/send!))

(rf/reg-event-fx
 ::moved
 ;; A mate moved, and we take what we follow of it. A tab we do not have is one
 ;; they made a moment ago whose write is still on its way, so the move waits for
 ;; it (see `steer`) rather than leaving us somewhere they are not. The rest is
 ;; taken only on the tab it was made on.
 (fn [{:keys [db]} [_ cid v]]
   (let [open   (get v [:ui :open])
         last   (second (get-in db [:follow :last]))
         place? (and (contains? v [:ui :open]) (not (contains? (off db) :place)))
         taken  (select-keys v (remove #{[:ui :open]} (paths db)))]
     (cond
       (not (party/mirrors? (:peers db) (me db) cid)) {}
       (and place? (not (pb/openable? db open)))
       {:db (update db :follow assoc :parked [cid v] :last [cid (merge last v)])}
       :else
       (let [switch? (and place? (not= open (get-in db [:ui :open])))
             db   (cond-> (update db :follow assoc :parked nil :last [cid (merge last v)])
                    switch? (pb/open open))
             ;; In facet order, so `:selections` lands after the `:selection`
             ;; it belongs to.
             db   (cond-> db
                    (= (get v ::tab) (get-in db [:ui :open]))
                    (as-> db (reduce (fn [db p]
                                       (if (contains? taken p)
                                         ((takes p #(assoc-in %1 (at %1 p) %2)) db (taken p))
                                         db))
                                     db (paths db))))
             n    (pb/frames db)
             f    (-> (get-in db [:playback :frame] 0) (max 0) (min (dec n)))
             db   (assoc-in db [:playback :frame] f)]
         {:db db
          ;; A new tab's sound arrives later, and `::pb/clock-ready` seeks it to
          ;; the frame, sets its rate and plays it if the db says so.
          :fx (when place?
                (if switch?
                  [[::pb/pause! nil]
                   [::pb/clock! {:id (:clip/current db) :sid open}]]
                  [[::pb/rate! (get-in db [:playback :rate] 1.0)]
                   [::pb/loop! (boolean (get-in db [:playback :loop?]))]
                   [::pb/seek! [(get-in db [:clip :fps]) n f]]
                   [(if (get-in db [:playback :playing?]) ::pb/play! ::pb/pause!) nil]]))})))))

(def ^:private steer
  "When an event changes what a party shares, the party hears where we are now.

  NOT ON A TICK, nor a move a mate made us make, which would only bounce back
  at them — nor one made by opening another document, which is a room the party
  is not in. And a move a mate made before its tab reached us is taken as soon
  as the write that brings it does."
  (rf/->interceptor
   :id ::steer
   :after (fn [ctx]
            (let [after (get-in ctx [:effects :db])]
              ;; On your own, which is nearly always, an event costs this and
              ;; nothing more.
              (if-not (and after (together? after))
                ctx
                (let [event  (first (get-in ctx [:coeffects :event]))
                      before (get-in ctx [:coeffects :db])
                      [_ v :as parked] (get-in after [:follow :parked])]
                  (cond-> ctx
                    (and parked (pb/openable? after (get v [:ui :open])))
                    (update-in [:effects :fx] (fnil conj []) [:dispatch (into [::moved] parked)])

                    (not (or (#{::pb/tick ::moved} event)
                             (not= (get-in before [:project :id]) (get-in after [:project :id]))
                             (= (view before (paths after)) (view after))))
                    (update-in [:effects :fx] (fnil conj []) [::nav! (view-msg after)]))))))))

;; ---------------------------------------------------------------------------
;; the room

(defn- wire-sid [sid] (some-> sid str (subs 1)))

(defn- who [db cid]
  {:who (or (get-in db [:peers cid :user]) "guest") :colour (collab/colour cid)})

(rf/reg-fx ::point (fn [[cid who m]] (page/seen! cid who m)))
(rf/reg-fx ::scroll (fn [m] (page/scrolled! m)))
(rf/reg-fx ::forget (fn [cid] (if (= :all cid) (page/clear!) (page/gone! cid))))

(rf/reg-event-fx
 ::heard
 (fn [{:keys [db]} [_ ^js m]]
   (let [cid  (.-cid m)
         mate (party/mirrors? (:peers db) (me db) cid)]
     (case (.-kind m)
       "reset"  {:db (cond-> (assoc-in db [:follow :parked] nil)
                       (.-elsewhere m) (update :presence dissoc :party))
                 ::forget :all}
       "leave"  {::forget cid}
       "state"  (let [joining (.-joining m)
                      ;; They followed us while we were on our own: take on the
                      ;; id they made of our cid. Nobody hosts — we both carry it.
                      adopt?  (and (me db) (= (me db) joining) (nil? (mine db))
                                   (get-in db [:presence :joinable]))
                      fx      (if adopt? (collab/say db {:party (.-party m)}) {:db db})
                      db      (:db fx)
                      r       (:peers db)
                      ours    (mine db)]
                  (cond-> fx
                    (not (party/mirrors? r (me db) cid)) (assoc ::forget cid)
                    ;; A newcomer in our party, and we drew the short straw: show
                    ;; them where we are, rather than leave them where they were
                    ;; until somebody moves.
                    (and joining ours (= ours (party/party-of r cid))
                         (= (me db) (party/responder r ours cid)))
                    (assoc ::nav! (view-msg db))))
       "nav"    {:dispatch [::moved cid (t/read reader (.-view m))]}
       "cursor" (if (and mate (not (contains? (off db) :pointers))
                         (= (.-open m) (wire-sid (get-in db [:ui :open]))))
                  {::point [cid (who db cid) (js->clj m :keywordize-keys true)]}
                  {::forget cid})
       "scroll" (if (and mate (not (contains? (off db) :scroll)))
                  {::scroll (js->clj m :keywordize-keys true)}
                  {})
       {}))))

;; ---------------------------------------------------------------------------
;; what you do

(rf/reg-event-fx
 ::follow
 (fn [{:keys [db]} [_ cid]]
   (if (party/joinable? (:peers db) (me db) cid)
     (collab/say db {:party (party/join-id (:peers db) cid)} {:joining cid})
     {})))

(rf/reg-event-fx
 ::leave
 (fn [{:keys [db]} _]
   (-> (collab/say (update db :follow dissoc :parked :last) {:party nil})
       (assoc ::forget :all))))

(rf/reg-event-fx
 ::set-joinable
 (fn [{:keys [db]} [_ on?]]
   (assoc (collab/say db {:joinable on?}) ::remember [:joinable (if on? "1" "0")])))

(rf/reg-event-fx
 ::toggle
 ;; Taking a facet back catches up on that facet — and only that one: the rest
 ;; of what they last said is as old as it is, and their frame, say, has moved
 ;; on since if they are playing.
 (fn [{:keys [db]} [_ k]]
   (let [was (off db)
         now (if (contains? was k) (disj was k) (conj was k))
         db  (assoc-in db [:follow :off] now)
         [cid v] (get-in db [:follow :last])]
     (cond-> {:db db ::remember [:off (.join (to-array (map name now)) ",")]}
       (= :pointers k) (assoc ::forget :all)
       (and (contains? was k) cid (facet-paths k))
       (assoc :dispatch [::moved cid (select-keys v (conj (facet-paths k) ::tab))])))))

(rf/reg-sub ::joinable? (fn [db _] (get-in db [:presence :joinable] true)))
(rf/reg-sub ::together? (fn [db _] (together? db)))
(rf/reg-sub ::off (fn [db _] (off db)))
(rf/reg-sub ::mates (fn [db _] (party/peers (:peers db) (me db))))
(rf/reg-sub ::may? (fn [db [_ cid]] (party/joinable? (:peers db) (me db) cid)))

(defn face
  "What following adds to a face in the roster: it is the whole control —
  follow them, or, once you are together, leave, which is the same one click."
  [{:keys [cid user]}]
  (let [who   (or user "guest")
        mate? (contains? @(rf/subscribe [::mates]) cid)
        may?  @(rf/subscribe [::may? cid])]
    {:class    (when mate? "mate")
     :disabled (not (or mate? may?))
     :title    (cond mate? (str "following " who " — click to stop")
                     may?  (str "follow " who)
                     :else (str who " isn't letting anyone follow"))
     :on-click #(rf/dispatch (if mate? [::leave] [::follow cid]))}))

(defn controls
  "The eye — whether others may follow you — and, while you are together, what
  you follow of them, each a switch."
  []
  (let [open? @(rf/subscribe [::joinable?])
        off   @(rf/subscribe [::off])]
    [:<>
     [:button.followable {:class (when open? "on")
                          :aria-label "let others follow me"
                          :title (if open?
                                   "others can follow you — click to stop them"
                                   "nobody can follow you — click to let them")
                          :on-click #(rf/dispatch [::set-joinable (not open?)])}
      [icon/view :eye]]
     (when @(rf/subscribe [::together?])
       [menu/popover {:label "▾" :title "what you follow" :class "follow-what"}
        (fn [_]
          [:div.view-popout
           (for [[k label] facets]
             ^{:key k}
             [:label [:input {:type "checkbox" :checked (not (contains? off k))
                              :on-change #(rf/dispatch [::toggle k])}]
              label])
           [:small "Unticked, you look around on your own — and don't move them either."]])])]))

;; ---------------------------------------------------------------------------

(defn- sharing
  "What the page layer sends right now, or nil when there is nobody to send it
  to. Read off app-db rather than subscribed: it is asked on a pointer move."
  []
  (let [db @rf-db/app-db]
    (when (together? db)
      {:open    (wire-sid (get-in db [:ui :open]))
       :scroll? (not (contains? (off db) :scroll))})))

(defn install!
  "Follow from now on. The one line in `arthur.core`."
  []
  (rf/dispatch-sync [::init])
  (rf/reg-global-interceptor steer)
  (collab/listen! ::heard)
  (page/install! {:send! collab/send! :sharing sharing}))
